import { LarixMarkets, LarixUserInfo } from "@projectlarix/larix-sdk";
import { useConnection } from "@solana/wallet-adapter-react";
import { convert } from "@stepfinance/common";
import type { LendingCollateralPosition, LendingCollateralPositionMap } from "@stepfinance/lending";
import { useEffect, useState } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";

import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLiquidity } from "../../contexts/liquidity";
import { useRefreshContext } from "../../contexts/refresh";
import type { IsolatedPoolPositionMap } from "../../types/lending";

export const useLarix = () => {
  const { connection } = useConnection();
  const { connected, publicKey } = useEnrichedWallet();
  const { raydiumPools } = useLiquidity();
  const { refreshID } = useRefreshContext();
  const [isolatedPools, setIsolatedPools] = useState<IsolatedPoolPositionMap>({});

  useEffect(() => setIsolatedPools({}), [publicKey]);

  useDeepCompareEffect(() => {
    const fetchPositions = async () => {
      if (publicKey) {
        const larixMarkets = new LarixMarkets(connection);
        await larixMarkets.loadReservesAndRewardApy();
        const larixUserInfo = new LarixUserInfo(publicKey, connection, larixMarkets.markets);
        await larixUserInfo.loadUserInfo();
        const poolsMap = {};
        const blankPosition: LendingCollateralPosition = {
          deposit: {
            amount: 0,
            apy: null,
          },
          borrow: {
            amount: 0,
            apy: null,
          },
          display: true,
        };
        larixUserInfo.marketDetails.forEach((item) => {
          const { mining, obligations } = item;
          const positionMap: LendingCollateralPositionMap = {};
          if (mining) {
            mining.detail.forEach((detail) => {
              const mintString = detail.reserve.state.info.liquidity.mintPubkey.toBase58();
              const amount = detail.amount.div(detail.reserve.exchangeRate).toNumber();
              // converting to string because supplyRewardApy type is number | BigNumber
              // multiply string by number converts to number.
              const position: LendingCollateralPosition = {
                ...blankPosition,
                deposit: {
                  amount,
                  // apy: supplyRewardApy + supplyApy,
                  // FIX ME - larix to fix supplyRewardApr for isolated pools
                  apy: 0,
                },
              };
              if (detail.reserve.state.info.isLP) {
                // const apy = lpFeesApr + lpApr + lpSupplyRewardApr;
                // FIX ME - larix to fix supplyRewardApr for isolated pools
                const apy = 0;
                const lp = raydiumPools[mintString];
                position.deposit = {
                  ...position.deposit,
                  apy,
                  mintA: lp.mints[0],
                  mintB: lp.mints[1],
                  // larix provides this price in USD, should be safe for toNumber()
                  tokenPrice: detail.reserve.liquidityPrice.toNumber(),
                };
              }
              positionMap[mintString] = position;
            });
          }
          if (obligations) {
            obligations.forEach((obligation) => {
              obligation.borrows.forEach((borrow, i) => {
                const mintString = borrow.reserve.state.info.liquidity.mintPubkey.toBase58();
                const borrowInfo = obligation.state.info.borrows[i];
                const borrowAmount = borrowInfo.borrowedAmountWads;
                const convertedBorrowAmount = convert(
                  borrowAmount,
                  borrow.reserve.state.info.liquidity.mintDecimals + 18,
                );
                const borrowPosition = {
                  amount: convertedBorrowAmount,
                  // apy: borrowApy - borrowRewardApy,
                  // FIX ME - larix to fix supplyRewardApr for isolated pools
                  apy: 0,
                };
                if (positionMap[mintString]) {
                  positionMap[mintString] = {
                    ...positionMap[mintString],
                    borrow: borrowPosition,
                  };
                } else {
                  positionMap[mintString] = {
                    ...blankPosition,
                    borrow: borrowPosition,
                  };
                }
              });
              obligation.deposits.forEach((deposit) => {
                const mintString = deposit.reserve.state.info.liquidity.mintPubkey.toBase58();
                const amount = deposit.amount.div(deposit.reserve.exchangeRate).toNumber();
                const depositPosition = {
                  amount,
                  // apy: supplyRewardApy + supplyApy,
                  // FIX ME - larix to fix supplyRewardApr for isolated pools
                  apy: 0,
                };
                if (positionMap[mintString]) {
                  positionMap[mintString] = {
                    ...positionMap[mintString],
                    deposit: depositPosition,
                  };
                } else {
                  positionMap[mintString] = {
                    ...blankPosition,
                    deposit: depositPosition,
                  };
                }
              });
            });
          }
          if (Object.keys(positionMap).length > 0) {
            let marketName = item.market.config.name;
            marketName = marketName[0].toUpperCase() + marketName.substring(1);
            poolsMap[marketName] = positionMap;
          }
        });
        setIsolatedPools(poolsMap);
      }
    };

    if (connected && Object.keys(raydiumPools).length > 0) {
      void fetchPositions();
    }
  }, [connected, publicKey, connection, raydiumPools, refreshID]);
  return { isolatedPools, collateral: {}, yieldFarms: {} };
};
